import classesData from '@/data/classes.json';
import type { ClassData, ClassGroup } from '@/data/types';

const classes = classesData as ClassData[];

function hasLevelGroups(groups: ClassGroup[] | undefined): boolean {
  if (!groups?.length) return false;
  return groups.some((g) => /beginner|intermediate|advanced|level/i.test(g.label));
}

const DISCIPLINE_STYLES: Record<string, { badge: string; hover: string; schedule: string }> = {
  'Calisthenics': {
    badge:    'bg-teal-500/15 text-teal-300 border border-teal-500/30',
    hover:    'hover:border-teal-500/40',
    schedule: 'border-teal-500/25 bg-[#0d1514]',
  },
  'CrossFit': {
    badge:    'bg-orange-500/15 text-orange-300 border border-orange-500/30',
    hover:    'hover:border-orange-500/40',
    schedule: 'border-orange-500/25 bg-[#140e00]',
  },
  'Mobility': {
    badge:    'bg-green-500/15 text-green-300 border border-green-500/30',
    hover:    'hover:border-green-500/40',
    schedule: 'border-green-500/25 bg-[#0a140a]',
  },
  'Kids Class': {
    badge:    'bg-purple-500/15 text-purple-300 border border-purple-500/30',
    hover:    'hover:border-purple-500/40',
    schedule: 'border-purple-500/25 bg-[#100a14]',
  },
};

const DEFAULT_STYLE = {
  badge:    'bg-gray-500/15 text-gray-300 border border-gray-500/30',
  hover:    'hover:border-gray-500/40',
  schedule: 'border-white/[0.08] bg-[#111]',
};

function getDisciplineStyle(name: string) {
  for (const key of Object.keys(DISCIPLINE_STYLES)) {
    if (name.toLowerCase().includes(key.toLowerCase())) return DISCIPLINE_STYLES[key];
  }
  return DEFAULT_STYLE;
}

export default function Classes() {
  return (
    <section id="classes" className="bg-black py-16 lg:py-32 px-4 lg:px-16">
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-12 lg:mb-16 text-center">
        <span data-animate className="inline-flex items-center gap-2 text-[#14B8A6] text-xs tracking-widest font-medium mb-4">
          <span className="w-4 h-px bg-[#14B8A6]" />
          TRAINING
          <span className="w-4 h-px bg-[#14B8A6]" />
        </span>
        <h2 data-animate data-delay="1" className="text-white text-3xl sm:text-4xl lg:text-6xl font-bold mb-4">Training Classes</h2>
        <p data-animate data-delay="2" className="text-gray-400 text-base lg:text-lg max-w-xl mx-auto leading-relaxed">
          Choose from our range of specialized classes — designed for athletes at every level.
        </p>
      </div>

      {/* Classes Grid */}
      <div className="max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
        {classes.map((classItem, index) => {
          const showLevelsLine = hasLevelGroups(classItem.groups);
          const delay = ((index % 3) + 1) as 1 | 2 | 3;
          const style = getDisciplineStyle(classItem.name);

          return (
            <article
              key={classItem.id}
              data-animate
              data-delay={delay}
              className={`bg-[#141414] rounded-3xl overflow-hidden hover:-translate-y-1 transition-all duration-300 hover:shadow-[0_20px_50px_-12px_rgba(0,0,0,0.6)] group flex flex-col border border-white/[0.07] ${style.hover} transition-colors`}
            >
              {/* Image + discipline badge */}
              <div className="relative w-full h-44 lg:h-48 overflow-hidden flex-shrink-0">
                <img
                  src={classItem.image}
                  alt={`${classItem.name} class at HUB30 gym in Jbeil, Lebanon`}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                {/* Discipline color badge */}
                <span className={`absolute top-3 right-3 text-[11px] font-semibold px-2.5 py-1 rounded-full backdrop-blur-sm ${style.badge}`}>
                  {classItem.name}
                </span>
              </div>

              <div className="p-6 flex flex-col flex-1 min-h-0">
                {/* Name */}
                <h3 className="text-white text-lg sm:text-xl font-bold tracking-tight leading-snug mb-3">
                  {classItem.name}
                </h3>

                {/* Description + optional levels */}
                <div className="mb-5 space-y-2">
                  <p className="text-gray-400 text-sm leading-relaxed">{classItem.description}</p>
                  {showLevelsLine && classItem.groups && (
                    <p className="text-gray-500 text-xs leading-relaxed">
                      <span className="font-medium">Levels: </span>
                      {classItem.groups.map((g) => g.label).join(' · ')}
                    </p>
                  )}
                </div>

                {/* Schedule panel */}
                <div className={`rounded-2xl border px-4 py-4 mb-6 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)] ${style.schedule}`}>
                  <div className="flex items-center gap-2 mb-4 pb-3 border-b border-white/[0.08]">
                    <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-white/5 text-gray-400" aria-hidden>
                      <i className="ri-calendar-line text-sm" />
                    </span>
                    <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-gray-400">
                      Schedule
                    </span>
                  </div>

                  <dl className="space-y-4 text-sm">
                    <div>
                      <dt className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-1.5">Days</dt>
                      <dd className="text-gray-100 leading-snug tabular-nums">{classItem.days}</dd>
                    </div>

                    {classItem.groups && classItem.groups.length > 0 ? (
                      <div className="pt-1 border-t border-white/[0.06]">
                        <dt className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-2.5">
                          {classItem.groupsTitle ?? 'Sessions'}
                        </dt>
                        <dd>
                          <ul className="space-y-2.5">
                            {classItem.groups.map((g) => (
                              <li
                                key={`${g.label}-${g.time}`}
                                className="grid grid-cols-1 min-[400px]:grid-cols-[minmax(0,1fr)_auto] gap-x-4 gap-y-0.5 items-baseline"
                              >
                                <span className="text-gray-300 text-sm leading-snug">{g.label}</span>
                                <span className="text-gray-400 text-sm tabular-nums min-[400px]:text-right">{g.time}</span>
                              </li>
                            ))}
                          </ul>
                        </dd>
                      </div>
                    ) : classItem.times ? (
                      <div className="pt-1 border-t border-white/[0.06]">
                        <dt className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-1.5">Time</dt>
                        <dd className="text-gray-100 tabular-nums">{classItem.times}</dd>
                      </div>
                    ) : null}
                  </dl>
                </div>

                {/* Coach — pinned to bottom */}
                <div className="flex items-center gap-3 mt-auto pt-4 border-t border-white/[0.06]">
                  {classItem.coaches ? (
                    <div className="flex -space-x-3 flex-shrink-0">
                      {classItem.coaches.map((c) => (
                        <img
                          key={c.name}
                          src={c.image}
                          alt={`${c.name} — HUB30 coach`}
                          loading="lazy"
                          decoding="async"
                          className="w-9 h-9 rounded-full object-cover object-top border-2 border-black"
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="w-9 h-9 bg-[#14B8A6]/20 rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="ri-user-line text-[#14B8A6] text-sm" aria-hidden />
                    </div>
                  )}
                  <div className="min-w-0">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-0.5">Coach</p>
                    <p className="text-white text-sm leading-snug truncate font-medium" title={classItem.coach}>
                      {classItem.coach}
                    </p>
                  </div>
                </div>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}
